import React, { memo, useEffect } from 'react';
import classnames from 'classnames';
import { ValidatedInput as Input } from '@glu/form-components';
import isEqual from 'lodash/isEqual';

import useLocale from 'components/useLocale/useLocale';

import { DROPDOWN_OPTION_NONE, NONE, POSITION_FIELD_MAX_LENGTH, TABLE_FIELDS } from '../constants';
import DelimitedDropdown from '../DelimitedDropdown';
import useStyles from '../Table.styles';
import {
  HARDCODED_LENGTH_FIELDS_LENGTH,
  LENGTH_MAX_LENGTH,
  MAP_FORMAT,
  START_POSITION_MAX_LENGTH,
  validateLengthTableField
} from '../utils.mapFormats';
import SharedRowPart from '../SharedRowPart';
import {
  asyncExecuteMultiplyGluFunctions,
  asyncRegisterMultiplyGluValidators,
  clearComponent
} from '../utils';

const mountValidator = ({ condition, emptyAddValue, hasPositionFields, validator }) => {
  if (hasPositionFields || !emptyAddValue) {
    return null;
  }

  if (!hasPositionFields && emptyAddValue) {
    return validator;
  }

  return condition ? validator : null;
};

const TransactionCodesRowComponent = ({
  cellClass,
  changeTransactionFieldVariant,
  field,
  formState,
  handlers,
  isModeUpdateOnly,
  mapFormat,
  onChange,
  onPositionFieldChange,
  onPositionFieldFocus,
  processMode,
  registerValidators,
  sending,
  transactionFieldVariant,
  turnOnCheckboxes,
  unregisterValidators,
  validate,
  validators
}) => {
  const locale = useLocale();
  const classes = useStyles();
  const { handleFixedAddValueFieldFocus, onFixedFieldFocus } = handlers.fixed;
  const { handleDelimitedAddValueFieldFocus } = handlers.delimited;

  const isPositionFieldDisabled =
    sending ||
    (formState[`${field.id}_addValue`] && formState[`${field.id}_addValue`][0]?.id !== NONE);

  const isAddValueDisabled =
    sending ||
    (mapFormat === MAP_FORMAT.DELIMITED
      ? !!formState[field.id]
      : !!formState[`${field.id}_startPosition`] || !!formState[`${field.id}_length`]);

  const delimitedPosition = formState[field.id];
  const fixedPosition = formState[`${field.id}_startPosition`];
  const fixedLength = formState[`${field.id}_length`];
  const addValueField = formState[`${field.id}_length`];

  const mountInitialValidation = () => {
    const hasPositionFields =
      mapFormat === MAP_FORMAT.DELIMITED ? delimitedPosition : fixedPosition && fixedLength;
    const hasAddValueField = addValueField?.[0]?.id ?? null;
    const emptyAddValue = !hasAddValueField || hasAddValueField === NONE;

    const { addValue, lengthValidation, position, startPosition } = validators;

    const hasOneOfPositions = mapFormat === MAP_FORMAT.FIXED && (fixedPosition || fixedLength);

    const readyLengthValidator = mountValidator({
      emptyAddValue,
      hasPositionFields,
      condition: !hasPositionFields,
      validator: lengthValidation(field),
      processMode
    });

    const fieldsToRegister = [
      {
        name: `${field.id}_addValue`,
        validator: !hasOneOfPositions
          ? mountValidator({
              emptyAddValue,
              hasPositionFields,
              condition: emptyAddValue,
              validator: addValue,
              processMode
            })
          : null
      },
      ...(mapFormat === MAP_FORMAT.DELIMITED
        ? [
            {
              name: field.id,
              validator: mountValidator({
                emptyAddValue,
                hasPositionFields,
                condition: !hasPositionFields,
                validator: position(field),
                processMode
              })
            }
          ]
        : []),
      ...(mapFormat === MAP_FORMAT.FIXED
        ? [
            {
              name: `${field.id}_startPosition`,
              validator: mountValidator({
                emptyAddValue,
                hasPositionFields,
                condition: !hasPositionFields,
                validator: startPosition(field),
                processMode
              })
            },
            {
              name: `${field.id}_length`,
              validator: [
                ...(readyLengthValidator?.length ? readyLengthValidator : []),
                validateLengthTableField(locale, field.fixedFormatLength)
              ]
            }
          ]
        : [])
    ];
    const fieldsToUnregister = [
      ...(mapFormat === MAP_FORMAT.FIXED
        ? [field.id]
        : [`${field.id}_startPosition`, `${field.id}_length`]),
      `${field.id}_addValue`
    ];

    asyncExecuteMultiplyGluFunctions(fieldsToUnregister, unregisterValidators)
      .then(() => asyncExecuteMultiplyGluFunctions(fieldsToUnregister, validate))
      .then(() => asyncRegisterMultiplyGluValidators(fieldsToRegister, registerValidators));
  };

  useEffect(() => {
    mountInitialValidation();

    return () => {
      clearComponent({ field, onChange, processMode, unregisterValidators });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={classes.row}>
      <SharedRowPart
        field={field}
        fieldNameClassName={cellClass}
        sending={sending}
        formState={formState}
        processMode={processMode}
        turnOnCheckboxes={turnOnCheckboxes}
        onChange={onChange}
        changeTransactionFieldVariant={changeTransactionFieldVariant}
        transactionFieldVariant={transactionFieldVariant}
      />

      {mapFormat === MAP_FORMAT.DELIMITED ? (
        <div className={classnames(cellClass, classes.positionField)}>
          <Input
            name={field.id}
            data-qa={field.id}
            type="number"
            min="1"
            onFocus={onPositionFieldFocus}
            onChange={onPositionFieldChange}
            maxLengthIndicatorLimit={POSITION_FIELD_MAX_LENGTH}
            disabled={isPositionFieldDisabled}
          />
        </div>
      ) : (
        <>
          <div className={classnames(cellClass, classes.positionField)}>
            <Input
              name={`${field.id}_startPosition`}
              data-qa={`${field.id}_startPosition`}
              type="number"
              min="1"
              onFocus={onFixedFieldFocus}
              onChange={onPositionFieldChange}
              maxLengthIndicatorLimit={START_POSITION_MAX_LENGTH}
              disabled={isPositionFieldDisabled}
            />
          </div>
          <div className={classnames(cellClass, classes.positionField)}>
            <Input
              name={`${field.id}_length`}
              data-qa={`${field.id}_length`}
              type="number"
              min="1"
              onFocus={onFixedFieldFocus}
              onChange={onPositionFieldChange}
              maxLengthIndicatorLimit={
                HARDCODED_LENGTH_FIELDS_LENGTH[field.id] || LENGTH_MAX_LENGTH
              }
              disabled={isPositionFieldDisabled}
            />
          </div>
        </>
      )}

      <div className={classnames(cellClass, classes.addValueField)}>
        <DelimitedDropdown
          className={classnames(classes.dropdown)}
          dataQa={`${field.id}_addValue`}
          name={`${TABLE_FIELDS.TRAN_CODE}_addValue`}
          defaultValue={
            !formState[`${field.id}_addValue`]?.length ? [DROPDOWN_OPTION_NONE] : undefined
          }
          options={field?.dropdownData}
          onChange={() => {
            const isNoneOptionSelected = formState[`${field.id}_addValue`]?.[0]?.id === NONE;

            if (!formState[`${field.id}_addValue`] || isNoneOptionSelected) {
              if (mapFormat === MAP_FORMAT.FIXED && !isModeUpdateOnly) {
                return handleFixedAddValueFieldFocus(field);
              }
              return !isModeUpdateOnly
                ? handleDelimitedAddValueFieldFocus(field, validators.addValue)
                : null;
            }
            return null;
          }}
          disabled={isAddValueDisabled}
        />
      </div>
    </div>
  );
};

export default memo(TransactionCodesRowComponent, (prevProps, nextProps) =>
  isEqual(prevProps.formState, nextProps.formState)
);
